import React from 'react';
import { BsX, BsCheck, BsPlusCircle } from 'react-icons/bs';
import { BiPencil } from 'react-icons/bi';

interface HelpDrawerMembrosProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ marginBottom: '20px' }}>
    <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '8px', borderBottom: '1px solid #eee', paddingBottom: '6px' }}>
      {title}
    </h4>
    <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6' }}>{children}</div>
  </div>
);

const ActionItem: React.FC<{ icon: React.ReactNode; label: string; description: string; bgColor: string; borderColor?: string; textColor?: string }> = ({ icon, label, description, bgColor, borderColor, textColor }) => (
  <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '10px' }}>
    <span style={{
      fontSize: '12px',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '28px',
      height: '28px',
      borderRadius: '3px',
      border: `1px solid ${borderColor || '#ccc'}`,
      backgroundColor: bgColor,
      color: textColor || 'black',
    }}>{icon}</span>
    <div>
      <strong>{label}</strong>
      <div style={{ color: '#666', fontSize: '12px' }}>{description}</div>
    </div>
  </div>
);

const DRAWER_WIDTH = 320;

const HelpDrawerMembros: React.FC<HelpDrawerMembrosProps> = ({ isOpen, onClose, children }) => {
  return (
    <>
      <div style={{ marginRight: isOpen ? DRAWER_WIDTH : 0, transition: 'margin-right 0.3s ease' }}>
        {children}
      </div>

      <div style={{
        position: 'fixed',
        top: 0,
        right: isOpen ? 0 : -DRAWER_WIDTH,
        width: DRAWER_WIDTH,
        height: '100vh',
        backgroundColor: '#fff',
        borderLeft: '1px solid #e0e0e0',
        boxShadow: '-2px 0 8px rgba(0,0,0,0.08)',
        transition: 'right 0.3s ease',
        zIndex: 1050,
        display: 'flex',
        flexDirection: 'column',
      }}>
        <div style={{
          padding: '12px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'linear-gradient(180deg, #2a2d3a 0%, #3c3f4c 100%)',
          color: '#fff',
        }}>
          <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>Ajuda — Membros CAVU</h3>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '18px', cursor: 'pointer', padding: '0 4px' }}
          >
            <BsX />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px', fontSize: '13px' }}>
          <Section title="Visão Geral">
            <p style={{ margin: 0 }}>
              Gerencie as pessoas cadastradas como membros da CAVU (Comissão de Acompanhamento e Verificação da Unicamp). Essas pessoas podem ser selecionadas para compor bancas de heteroidentificação.
            </p>
          </Section>

          <Section title="Filtros">
            <ul style={{ margin: 0, paddingLeft: '18px' }}>
              <li><strong>Nome:</strong> Busca pelo nome da pessoa.</li>
              <li><strong>Email:</strong> Busca pelo email.</li>
              <li><strong>Telefone:</strong> Busca pelo telefone.</li>
              <li><strong>Vínculo:</strong> Filtra por tipo de vínculo (Docente, Servidor, Estudante, etc.).</li>
              <li><strong>Status:</strong> Ativos, Inativos ou Todos.</li>
            </ul>
            <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#888' }}>
              Filtros aplicados automaticamente conforme você digita.
            </p>
          </Section>

          <Section title="Colunas da Tabela">
            <ul style={{ margin: 0, paddingLeft: '18px' }}>
              <li><strong>Nome:</strong> Nome completo da pessoa.</li>
              <li><strong>Email:</strong> Email de contato.</li>
              <li><strong>Telefone:</strong> Telefone de contato.</li>
              <li><strong>Vínculo:</strong> Tipo de vínculo institucional.</li>
              <li><strong>CPF:</strong> Documento da pessoa.</li>
              <li><strong>Matrícula:</strong> Matrícula institucional.</li>
              <li><strong>Status:</strong> Ativo (verde) ou Inativo (vermelho).</li>
            </ul>
          </Section>

          <Section title="Ações">
            <ActionItem icon={<BiPencil />} label="Editar" description="Edita os dados da pessoa (nome, email, telefone, vínculo, etc.)." bgColor="#e3f2fd" borderColor="gray" />
            <ActionItem icon={<BsX />} label="Desativar" description="Desativa a pessoa. Ela não aparecerá mais nas consultas de disponibilidade." bgColor="#f8d7da" borderColor="gray" />
            <ActionItem icon={<BsCheck />} label="Ativar" description="Reativa uma pessoa desativada, tornando-a disponível novamente para bancas." bgColor="#d4edda" borderColor="gray" />
            <ActionItem icon={<BsPlusCircle style={{ color: 'white' }} />} label="Nova Pessoa" description="Cadastra uma nova pessoa como membro CAVU. Requer permissão 'cadastrar-pessoa'." bgColor="#28a745" borderColor="#28a745" textColor="white" />
          </Section>

          <Section title="Vínculos">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px' }}>
              {[
                { vinculo: 'Docente', desc: 'Professor(a) da universidade.' },
                { vinculo: 'Servidor', desc: 'Servidor técnico-administrativo.' },
                { vinculo: 'Est. Graduação', desc: 'Estudante de graduação.' },
                { vinculo: 'Est. Pós-Grad.', desc: 'Estudante de pós-graduação.' },
                { vinculo: 'Soc. Civil', desc: 'Representante da sociedade civil organizada.' },
              ].map(({ vinculo, desc }) => (
                <div key={vinculo} style={{ display: 'flex', gap: '8px' }}>
                  <strong style={{ minWidth: '95px' }}>{vinculo}:</strong>
                  <span>{desc}</span>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Dicas">
            <ul style={{ margin: 0, paddingLeft: '18px' }}>
              <li>Pessoas desativadas não aparecem na consulta de disponibilidade de bancas.</li>
              <li>Use o filtro "Inativos" para encontrar pessoas desativadas e reativá-las.</li>
              <li>O vínculo define em qual categoria a pessoa pode ser selecionada como titular na composição da banca.</li>
            </ul>
          </Section>
        </div>
      </div>
    </>
  );
};

export default HelpDrawerMembros;
